import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ExternalLinkIcon } from '../../../ui/Icons';
import {
  WORKSPACES,
  BADGE_CLASSES,
  type Workspace,
} from '../../../../config/workspaces';

export interface WorkspaceSwitcherProps {
  className?: string;
}

/** "CURRENT" pill — the only badge in active use (spec §6). */
const CurrentBadge: React.FC = () => (
  <span
    className={`text-xs px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide ${BADGE_CLASSES.CURRENT}`}
  >
    CURRENT
  </span>
);

/** Shared row internals (icon tile + text block), used by both variants. */
const WorkspaceRow: React.FC<{ workspace: Workspace }> = ({ workspace }) => {
  const Icon = workspace.icon;

  return (
    <>
      {/* Icon tile */}
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center">
        <Icon
          size={20}
          className="text-secondary-600 dark:text-secondary-300"
        />
      </div>

      {/* Text block */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-secondary-900 dark:text-white truncate">
            {workspace.name}
          </span>
          {workspace.isCurrent ? (
            <CurrentBadge />
          ) : (
            // External-link glyph — only on sibling (link) rows.
            <ExternalLinkIcon
              size={12}
              strokeWidth={2}
              className="flex-shrink-0 text-secondary-400"
            />
          )}
        </div>
        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
          {workspace.description}
        </p>
      </div>
    </>
  );
};

/**
 * WorkspaceSwitcher renders the cross-product "Workspaces" dropdown described
 * in docs/workspace-switcher-spec.md. All three ecosystem surfaces are listed;
 * the current product (Agent Designer) is a non-interactive "you are here" row,
 * and the siblings open in a new tab.
 */
export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const baseRow = 'flex items-start gap-3 p-3 rounded-lg transition-colors';
  // Sibling links get a hover background; the current row carries a persistent
  // "you are here" background and no hover change.
  const siblingRow = `${baseRow} hover:bg-secondary-50 dark:hover:bg-secondary-700/50`;
  const currentRow = `${baseRow} bg-secondary-50 dark:bg-secondary-700/50`;

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        onClick={() => setIsOpen((open) => !open)}
        className={`
          relative inline-flex items-center gap-1 px-3 py-2 rounded-lg
          text-sm font-medium transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          dark:focus:ring-offset-secondary-900
          ${
            isOpen
              ? 'text-secondary-900 dark:text-white after:absolute after:bottom-1 after:left-3 after:right-3 after:h-0.5 after:rounded-full after:bg-primary-500'
              : 'text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white'
          }
        `
          .trim()
          .replace(/\s+/g, ' ')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Switch workspace"
      >
        Workspaces
        <ChevronDownIcon
          size={16}
          strokeWidth={2}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className="
            absolute right-0 mt-2 w-72 p-2 rounded-xl z-50
            bg-white dark:bg-secondary-800
            shadow-lg ring-1 ring-black/5 dark:ring-white/10
          "
          role="menu"
          aria-orientation="vertical"
        >
          {/* Section header */}
          <div className="px-3 py-2 mb-2 border-b border-secondary-100 dark:border-secondary-700">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400">
              GraymatterLab Ecosystem
            </span>
          </div>

          {/* Rows */}
          {WORKSPACES.map((workspace) =>
            workspace.isCurrent ? (
              <div
                key={workspace.id}
                className={currentRow}
                role="menuitem"
                aria-current="page"
              >
                <WorkspaceRow workspace={workspace} />
              </div>
            ) : (
              <a
                key={workspace.id}
                href={workspace.url}
                target="_blank"
                rel="noopener noreferrer"
                className={siblingRow}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                <WorkspaceRow workspace={workspace} />
              </a>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
